import IconButton from "@material-ui/core/IconButton";
import AddPhotoIcon from "@material-ui/icons/AddPhotoAlternate";
import React, {useMemo, useRef, useState} from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import {fade} from "@material-ui/core/styles/colorManipulator";
import {useRecentImages} from "../../../hooks/recentimages";
import useImageUpload from "../../../hooks/imageupload";
import {Trans, useTranslation} from "react-i18next";
import useTemplateInstance from "../../../hooks/useTemplateInstance";
import {ACTIONS, getUserImageResources, getUserResourcesAsList} from "../../../../Providers/TemplateInstanceProvider";
import EpmsImage from "../../../../Common/EpmsImage";
import UploadProgressDialog from "../../UploadProgressDialog";
import Icon from "@material-ui/core/Icon";
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@material-ui/core";
import DialogContentText from "@material-ui/core/DialogContentText";
import Button from "@material-ui/core/Button";



const useStyles = makeStyles(theme => ({
    toolbarItems: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        maxWidth: '100%',
        overflowX: 'auto',
        display: 'flex',
        flexWrap: 'nowrap',
        '& > li.image-container > button': {
            width: '76px',
            height: '76px',
            marginLeft: theme.spacing(1),
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
            borderRadius: '0',
            overflow: 'hidden',
            position: 'relative',
            '& > img': {
                width: '100%',
                height: '100%',
                objectFit: 'cover'
            }
        },
    },
    inboxContainer: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    selectedImage: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        backgroundColor: theme.palette.primary.main + 'aa'
    },
    noImagesContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& > div': {
            paddingRight: theme.spacing(4),
            paddingLeft: theme.spacing(4),
            boxSizing: 'border-box',
            textAlign: 'center'
        }
    },
    addImagesButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        width: '76px',
        height: '76px',
        borderRadius: '0px',
        border: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        '& > div': {
            fontSize: '12px'
        }
    },
    imageIsUsed: {
        position: 'absolute',
        right: '2px',
        bottom: '2px',
        color: 'rgba(255, 255, 255, 0.8)',
        '& span': {
            width: '1em',
            height: '1em',
            fontSize: '1rem'
        }
    }
}))


export default ({handleSelectImage, resource}) => {
    const classes = useStyles();
    const [state, dispatch] = useTemplateInstance();
    const { image } = resource || {image: {}};
    const [notAllUploadedDialog, setNotAllUploadedDialog] = useState(false);

    const { recentImages, addImage } = useRecentImages();
    const onUploadComplete = (images, allUploaded) => {
        if (!allUploaded) {
            setNotAllUploadedDialog(true);
        }
        for (const image of images) {
            addImage({
                uuid: image.uuid,
                width: image.dimensions.width,
                height: image.dimensions.height
            });
        }
        if (images.length == 1) {
            const image = images[0];
            changeImage({
                uuid: image.uuid,
                width: image.dimensions.width,
                height: image.dimensions.height
            })
        }
    }
    const { totalProgress, isUploading, retrying, bind } = useImageUpload(null, onUploadComplete);
    const fileRef = useRef();

    const usedImageUuids = useMemo(() => {
        if (!resource) {
            return [];
        }
        const resources = getUserImageResources(state);
        const uuids = resources
            .filter(r => {
                // Don't add the current resource as we work on a local version:
                return (r?.image?.uuid && resource.fullId != r.fullId)
            })
            .map(resource => {
                return resource.image.uuid
            });
        if (image?.uuid) {
            uuids.push(image.uuid)
        }
        return uuids;
    }, [resource])


    // Reverse the list so newest images is first.
    const reversedImageList = useMemo(() => {
        return recentImages.slice().reverse()
    }, [recentImages])

    const handleAddButtonClick = () => {
        console.log('File ref', fileRef)
        fileRef.current.click();
    }

    const changeImage = (image) => {
        handleSelectImage(image)
    }

    return <div className={classes.inboxContainer}>
        <input type='file' ref={fileRef} {...bind} multiple={true} style={{position: 'absolute', left: '-9000'}}/>
        <ul className={classes.toolbarItems}>
            <li>
                <button className={classes.addImagesButton} onClick={handleAddButtonClick}>
                    <div>{reversedImageList.length == 0 ? <Trans>Upload</Trans> : <Trans>Upload more</Trans>}</div>
                    <AddPhotoIcon/>
                </button>
            </li>
            {reversedImageList.map(userImage => {
                return <li key={userImage.uuid} className='image-container'>
                    <ImageContainer userImage={userImage}
                                    onClick={changeImage}
                                    isSelected={image && image.uuid == userImage.uuid}
                                    usedImageUuids={usedImageUuids}
                    />
                </li>
            })}
            {reversedImageList.length == 0 && <li className={classes.noImagesContainer}>
                <div>
                    <div><Trans>The image inbox is empty</Trans></div>
                </div>
            </li>}
        </ul>
        {isUploading && <UploadProgressDialog retrying={retrying} totalProgress={totalProgress} />}
        {notAllUploadedDialog && <NotAllImagesUploadedDialog onClose={() => setNotAllUploadedDialog(false)}/>}
    </div>
}

const NotAllImagesUploadedDialog = ({onClose}) => {

    return <Dialog open={true} onClose={onClose}>
        <DialogTitle>
            <Trans>Upload failed</Trans>
        </DialogTitle>
        <DialogContent>
            <DialogContentText>
                <Trans>At least one of the files couldn't be uploaded due to an issue with your Internet connection.</Trans>
            </DialogContentText>
            <DialogContentText>
                <Trans>Please check your connection and try again.</Trans>
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button variant='contained' onClick={onClose}>
                <Trans>Ok</Trans>
            </Button>
        </DialogActions>
    </Dialog>
}

const ImageContainer = ({onClick, userImage, isSelected, usedImageUuids}) => {
    const isUsed = useMemo(() => {
        return (usedImageUuids.indexOf(userImage.uuid) >= 0 );
    }, [usedImageUuids]);


    const classes = useStyles();
    return <button onClick={() => onClick(userImage)}>
        <EpmsImage uuid={userImage.uuid} size={320}/>
        {isSelected&& <div className={classes.selectedImage}/>}
        {isUsed && <div className={classes.imageIsUsed}>
            <Icon>check_circle</Icon>
        </div>}
    </button>
}